import React, { useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useAppDispatch, useAppSelector } from "../../app/hooks";

import { useNavigate, useLocation } from "react-router";
import {
  getAllActiveBankAccounts,
  getAllActiveBanks,
  getTopupRequestByID,
  insertTopupRequest,
  updateTopupRequest,
} from "./services/topup.services";
import { ErrorToast } from "../../toast/Toasts";
import moment from "moment";
import { clearTopupRequestDetails } from "./topupSlice";
import { DEPOSITED_MODE } from "../../_constants/constant";

const TopupRequestForm = ({ isViewMode = false, setIsEdit }: any) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    dispatch(getAllActiveBanks());
    dispatch(getAllActiveBankAccounts());
  }, []);

  const bankList: any[] = useAppSelector((state) => state.topup.BankList);
  const bankAccountList: any[] = useAppSelector(
    (state) => state.topup.BankAccountList || []
  );
  const topupRequestDetails = useAppSelector(
    (state) => state.topup.TopupRequestDetail
  );
  const TopupID = (location as any)?.state?.ID || null;

  const formik = useFormik({
    initialValues: {
      ...topupRequestDetails,
      TransactionDate: topupRequestDetails.TransactionDate
        ? moment(topupRequestDetails.TransactionDate).format("YYYY-MM-DD")
        : "",
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      Type: Yup.string().required("Top-up type is required"),

      Amount: Yup.number()
        .typeError("Amount must be a number")
        .required("Amount is required")
        .min(1, "Minimum ₹1")
        .max(1000000, "Maximum ₹10,00,000"),

      TransactionDate: Yup.string().when("Type", {
        is: (type: string) =>
          type === "Cheque" ||
          type === "Net Transfer" ||
          type === "Cash" ||
          type === "Other",
        then: (schema) => schema.required("Transaction Date is required"),
        otherwise: (schema) => schema.notRequired(),
      }),

      ChequeNo: Yup.string().when("Type", {
        is: "Cheque",
        then: (schema) => schema.required("Cheque No is required"),
        otherwise: (schema) => schema.notRequired(),
      }),

      BankID: Yup.mixed().when("Type", {
        is: (type: string) => type === "Cheque" || type === "Net Transfer",
        then: (schema) => schema.required("Bank is required"),
        otherwise: (schema) => schema.notRequired(),
      }),

      ChequeBranchName: Yup.string().when("Type", {
        is: "Cheque",
        then: (schema) => schema.required("Branch Name is required"),
        otherwise: (schema) => schema.notRequired(),
      }),

      AccHoloderName: Yup.string().when("Type", {
        is: (type: string) => type === "Cheque" || type === "Net Transfer",
        then: (schema) => schema.required("Account Holder Name is required"),
        otherwise: (schema) => schema.notRequired(),
      }),

      AccountNo: Yup.string().when("Type", {
        is: (type: string) =>
          type === "Cheque" || type === "Net Transfer" || "Cash",
        then: (schema) => schema.required("Account No is required"),
        otherwise: (schema) => schema.notRequired(),
      }),

      NetTransferID: Yup.string().when("Type", {
        is: "Net Transfer",
        then: (schema) => schema.required("Net Transfer ID is required"),
        otherwise: (schema) => schema.notRequired(),
      }),

      TransactionNo: Yup.string().when("Type", {
        is: "Cash",
        then: (schema) => schema.required("Transaction No is required"),
        otherwise: (schema) => schema.notRequired(),
      }),

      DepositedBankID: Yup.mixed().when("Type", {
        is: (type: string) =>
          type === "Cheque" || type === "Net Transfer" || type === "Cash",
        then: (schema) => schema.required("Deposited Bank is required"),
        otherwise: (schema) => schema.notRequired(),
      }),

      DepositedBranch: Yup.string().when("Type", {
        is: "Cash",
        then: (schema) => schema.required("Deposited Branch is required"),
        otherwise: (schema) => schema.notRequired(),
      }),

      DepositedMode: Yup.string().when("Type", {
        is: (type: string) => type === "Other" || type === "Cash",
        then: (schema) => schema.required("Deposited Mode is required"),
        otherwise: (schema) => schema.notRequired(),
      }),
    }),
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        const payload = {
          BankID: values?.BankID ? Number(values.BankID) : null,
          DepositedBankID: values?.DepositedBankID
            ? Number(values.DepositedBankID)
            : null,
          Amount: Number(values.Amount),
          TransactionDate: values.TransactionDate || null,
          CustomerID: localStorage.getItem("user_id"),
          ChequeNo: values?.ChequeNo,
          DepositedBranch: values?.DepositedBranch,
          ChequeBranchName: values?.ChequeBranchName,
          AccHoloderName: values?.AccHoloderName,
          AccountNo: values?.AccountNo,
          Remarks: values?.Remarks,
          NetTransferID: values?.NetTransferID,
          TransactionNo: values?.TransactionNo,
          DepositedMode: values?.DepositedMode,
          Type: values?.Type,
        };

        const response: any = await dispatch(
          TopupID
            ? updateTopupRequest({ ...payload, ID: TopupID })
            : insertTopupRequest(payload)
        );

        if (response?.meta?.requestStatus === "fulfilled") {
          resetForm();
          dispatch(clearTopupRequestDetails());
          if (isViewMode) {
            setIsEdit(false);
            dispatch(getTopupRequestByID(TopupID));
          } else {
            navigate(-1);
          }
        } else {
          ErrorToast(response?.payload?.Message || "Failed to submit request");
        }
      } catch (err) {
        console.error(err);
        ErrorToast("An error occurred. Please try again.");
      } finally {
        setSubmitting(false);
      }
    },
  });

  const handleTypeChange = (type: string) => {
    if (isViewMode) return;
    const fieldsToClear = [
      "ChequeNo",
      "BankID",
      "ChequeBranchName",
      "AccHoloderName",
      "AccountNo",
      "NetTransferID",
      "TransactionNo",
      "DepositedBankID",
      "DepositedBranch",
      "DepositedMode",
    ];

    fieldsToClear.forEach((field) => {
      formik.setFieldValue(field, "");
    });

    formik.setFieldValue("Type", type);
  };

  const tabList = ["Cheque", "Net Transfer", "Cash", "Other"];

  const renderFields = () => {
    const { Type } = formik.values;

    switch (Type) {
      case "Cheque":
        return (
          <>
            <div className="row mb-3">
              <div className="col-md-4">
                <label className="form-label">
                  Cheque No <span className="text-danger">*</span>
                </label>
                <input
                  name="ChequeNo"
                  className={`form-control input-small ${
                    formik.touched.ChequeNo && formik.errors.ChequeNo
                      ? "is-invalid"
                      : ""
                  }`}
                  value={formik.values.ChequeNo}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.ChequeNo && formik.errors.ChequeNo && (
                  <div className="error-label">{formik.errors.ChequeNo}</div>
                )}
              </div>

              <div className="col-md-4">
                <label className="form-label">
                  Cheque Date Of Transction{" "}
                  <span className="text-danger">*</span>
                </label>
                <input
                  type="date"
                  name="TransactionDate"
                  className={`form-control input-small ${
                    formik.touched.TransactionDate &&
                    formik.errors.TransactionDate
                      ? "is-invalid"
                      : ""
                  }`}
                  value={(formik.values.TransactionDate || "")?.split("T")[0]}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.TransactionDate &&
                  formik.errors.TransactionDate && (
                    <div className="error-label">
                      {formik.errors.TransactionDate}
                    </div>
                  )}
              </div>

              <div className="col-md-4">
                <label className="form-label">
                  Deposited Bank <span className="text-danger">*</span>
                </label>
                <select
                  name="DepositedBankID"
                  className={`form-select input-small ${
                    formik.touched.DepositedBankID &&
                    formik.errors.DepositedBankID
                      ? "is-invalid"
                      : ""
                  }`}
                  value={formik.values.DepositedBankID || ""}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                >
                  <option value="">Select Bank</option>
                  {bankAccountList.map((b) => (
                    <option key={b.ID} value={b.ID}>
                      {`${b.BankName} - ${b.AccountNo}`}
                    </option>
                  ))}
                </select>
                {formik.touched.DepositedBankID &&
                  formik.errors.DepositedBankID && (
                    <div className="error-label">
                      {formik.errors.DepositedBankID}
                    </div>
                  )}
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-md-4">
                <label className="form-label">Deposited Branch</label>
                <input
                  name="DepositedBranch"
                  className="form-control input-small"
                  value={formik.values.DepositedBranch}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
              </div>

              <div className="col-md-4">
                <label className="form-label">
                  Bank <span className="text-danger">*</span>
                </label>
                <select
                  name="BankID"
                  value={formik.values.BankID || ""}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className={`form-select input-small ${
                    formik.touched.BankID && formik.errors.BankID
                      ? "is-invalid"
                      : ""
                  }`}
                >
                  <option value="">Select Bank</option>
                  {bankList.map((b) => (
                    <option key={b.ID} value={b.ID}>
                      {b.BankName}
                    </option>
                  ))}
                </select>
                {formik.touched.BankID && formik.errors.BankID && (
                  <div className="error-label">{formik.errors.BankID}</div>
                )}
              </div>

              <div className="col-md-4">
                <label className="form-label">
                  Cheque Branch Name <span className="text-danger">*</span>
                </label>
                <input
                  name="ChequeBranchName"
                  className={`form-control input-small ${
                    formik.touched.ChequeBranchName &&
                    formik.errors.ChequeBranchName
                      ? "is-invalid"
                      : ""
                  }`}
                  value={formik.values.ChequeBranchName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.ChequeBranchName &&
                  formik.errors.ChequeBranchName && (
                    <div className="error-label">
                      {formik.errors.ChequeBranchName}
                    </div>
                  )}
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-md-4">
                <label className="form-label">
                  Acc Holder Name <span className="text-danger">*</span>
                </label>
                <input
                  name="AccHoloderName"
                  className={`form-control input-small ${
                    formik.touched.AccHoloderName &&
                    formik.errors.AccHoloderName
                      ? "is-invalid"
                      : ""
                  }`}
                  value={formik.values.AccHoloderName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.AccHoloderName &&
                  formik.errors.AccHoloderName && (
                    <div className="error-label">
                      {formik.errors.AccHoloderName}
                    </div>
                  )}
              </div>

              <div className="col-md-4">
                <label className="form-label">
                  Account No <span className="text-danger">*</span>
                </label>
                <input
                  name="AccountNo"
                  className={`form-control input-small ${
                    formik.touched.AccountNo && formik.errors.AccountNo
                      ? "is-invalid"
                      : ""
                  }`}
                  value={formik.values.AccountNo}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.AccountNo && formik.errors.AccountNo && (
                  <div className="error-label">{formik.errors.AccountNo}</div>
                )}
              </div>

              <div className="col-md-4">
                <label className="form-label">
                  Amount (₹) <span className="text-danger">*</span>
                </label>
                <input
                  type="number"
                  name="Amount"
                  className={`form-control input-small ${
                    formik.touched.Amount && formik.errors.Amount
                      ? "is-invalid"
                      : ""
                  }`}
                  value={formik.values.Amount || ""}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.Amount && formik.errors.Amount && (
                  <div className="error-label">{formik.errors.Amount}</div>
                )}
              </div>
            </div>

            <div className="row mb-1">
              <div className="col-md-12">
                <label className="form-label">Remarks</label>
                <textarea
                  name="Remarks"
                  className={`form-control textarea-remarks ${
                    formik.touched.Remarks && formik.errors.Remarks
                      ? "is-invalid"
                      : ""
                  }`}
                  rows={3}
                  value={formik.values.Remarks}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.Remarks && formik.errors.Remarks && (
                  <div className="error-label">{formik.errors.Remarks}</div>
                )}
              </div>
            </div>
          </>
        );

      case "Net Transfer":
        return (
          <>
            <div className="row mb-3">
              <div className="col-md-4">
                <label className="form-label">
                  Net Transfer ID <span className="text-danger">*</span>
                </label>
                <input
                  name="NetTransferID"
                  className={`form-control input-small ${
                    formik.touched.NetTransferID && formik.errors.NetTransferID
                      ? "is-invalid"
                      : ""
                  }`}
                  value={formik.values.NetTransferID}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.NetTransferID &&
                  formik.errors.NetTransferID && (
                    <div className="error-label">
                      {formik.errors.NetTransferID}
                    </div>
                  )}
              </div>

              <div className="col-md-4">
                <label className="form-label">
                  Net Transfer Date Of Transaction
                  <span className="text-danger">*</span>
                </label>
                <input
                  type="date"
                  name="TransactionDate"
                  className={`form-control input-small ${
                    formik.touched.TransactionDate &&
                    formik.errors.TransactionDate
                      ? "is-invalid"
                      : ""
                  }`}
                  value={(formik.values.TransactionDate || "").split("T")[0]}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.TransactionDate &&
                  formik.errors.TransactionDate && (
                    <div className="error-label">
                      {formik.errors.TransactionDate}
                    </div>
                  )}
              </div>

              <div className="col-md-4">
                <label className="form-label">
                  Bank <span className="text-danger">*</span>
                </label>
                <select
                  name="BankID"
                  className={`form-select input-small ${
                    formik.touched.BankID && formik.errors.BankID
                      ? "is-invalid"
                      : ""
                  }`}
                  value={formik.values.BankID || ""}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                >
                  <option value="">Select Bank</option>
                  {bankList.map((b) => (
                    <option key={b.ID} value={b.ID}>
                      {b.BankName}
                    </option>
                  ))}
                </select>
                {formik.touched.BankID && formik.errors.BankID && (
                  <div className="error-label">{formik.errors.BankID}</div>
                )}
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-md-4">
                <label className="form-label">
                  Deposited Bank <span className="text-danger">*</span>
                </label>
                <select
                  name="DepositedBankID"
                  className={`form-select input-small ${
                    formik.touched.DepositedBankID &&
                    formik.errors.DepositedBankID
                      ? "is-invalid"
                      : ""
                  }`}
                  value={formik.values.DepositedBankID || ""}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                >
                  <option value="">Select Bank</option>
                  {bankAccountList.map((b) => (
                    <option key={b.ID} value={b.ID}>
                      {`${b.BankName} - ${b.AccountNo}`}
                    </option>
                  ))}
                </select>
                {formik.touched.DepositedBankID &&
                  formik.errors.DepositedBankID && (
                    <div className="error-label">
                      {formik.errors.DepositedBankID}
                    </div>
                  )}
              </div>

              <div className="col-md-4">
                <label className="form-label">
                  Acc Holder Name <span className="text-danger">*</span>
                </label>
                <input
                  name="AccHoloderName"
                  className={`form-control input-small ${
                    formik.touched.AccHoloderName &&
                    formik.errors.AccHoloderName
                      ? "is-invalid"
                      : ""
                  }`}
                  value={formik.values.AccHoloderName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.AccHoloderName &&
                  formik.errors.AccHoloderName && (
                    <div className="error-label">
                      {formik.errors.AccHoloderName}
                    </div>
                  )}
              </div>

              <div className="col-md-4">
                <label className="form-label">
                  Account No <span className="text-danger">*</span>
                </label>
                <input
                  name="AccountNo"
                  className={`form-control input-small ${
                    formik.touched.AccountNo && formik.errors.AccountNo
                      ? "is-invalid"
                      : ""
                  }`}
                  value={formik.values.AccountNo}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.AccountNo && formik.errors.AccountNo && (
                  <div className="error-label">{formik.errors.AccountNo}</div>
                )}
              </div>
            </div>

            <div className="row mb-1">
              <div className="col-md-4">
                <label className="form-label">
                  Amount (₹) <span className="text-danger">*</span>
                </label>
                <input
                  type="number"
                  name="Amount"
                  className={`form-control input-small ${
                    formik.touched.Amount && formik.errors.Amount
                      ? "is-invalid"
                      : ""
                  }`}
                  value={formik.values.Amount || ""}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.Amount && formik.errors.Amount && (
                  <div className="error-label">{formik.errors.Amount}</div>
                )}
              </div>
              <div className="col-md-8">
                <label className="form-label">Remarks</label>
                <textarea
                  name="Remarks"
                  className={`form-control textarea-remarks ${
                    formik.touched.Remarks && formik.errors.Remarks
                      ? "is-invalid"
                      : ""
                  }`}
                  rows={3}
                  value={formik.values.Remarks}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.Remarks && formik.errors.Remarks && (
                  <div className="error-label">{formik.errors.Remarks}</div>
                )}
              </div>
            </div>
          </>
        );

      case "Cash":
        return (
          <>
            <div className="row mb-3">
              <div className="col-md-4">
                <label className="form-label">
                  Transaction No <span className="text-danger">*</span>
                </label>
                <input
                  name="TransactionNo"
                  className={`form-control input-small ${
                    formik.touched.TransactionNo && formik.errors.TransactionNo
                      ? "is-invalid"
                      : ""
                  }`}
                  value={formik.values.TransactionNo}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.TransactionNo &&
                  formik.errors.TransactionNo && (
                    <div className="error-label">
                      {formik.errors.TransactionNo}
                    </div>
                  )}
              </div>

              <div className="col-md-4">
                <label className="form-label">
                  Cash Date Of Transction <span className="text-danger">*</span>
                </label>
                <input
                  type="date"
                  name="TransactionDate"
                  className={`form-control input-small ${
                    formik.touched.TransactionDate &&
                    formik.errors.TransactionDate
                      ? "is-invalid"
                      : ""
                  }`}
                  value={(formik.values.TransactionDate || "").split("T")[0]}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.TransactionDate &&
                  formik.errors.TransactionDate && (
                    <div className="error-label">
                      {formik.errors.TransactionDate}
                    </div>
                  )}
              </div>

              <div className="col-md-4">
                <label className="form-label">
                  Deposited Bank <span className="text-danger">*</span>
                </label>
                <select
                  name="DepositedBankID"
                  className={`form-select input-small ${
                    formik.touched.DepositedBankID &&
                    formik.errors.DepositedBankID
                      ? "is-invalid"
                      : ""
                  }`}
                  value={formik.values.DepositedBankID || ""}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                >
                  <option value="">Select Bank</option>
                  {bankAccountList.map((b) => (
                    <option key={b.ID} value={b.ID}>
                      {`${b.BankName} - ${b.AccountNo}`}
                    </option>
                  ))}
                </select>
                {formik.touched.DepositedBankID &&
                  formik.errors.DepositedBankID && (
                    <div className="error-label">
                      {formik.errors.DepositedBankID}
                    </div>
                  )}
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-md-4">
                <label className="form-label">
                  Deposited Branch <span className="text-danger">*</span>
                </label>
                <input
                  name="DepositedBranch"
                  className={`form-control input-small ${
                    formik.touched.DepositedBranch &&
                    formik.errors.DepositedBranch
                      ? "is-invalid"
                      : ""
                  }`}
                  value={formik.values.DepositedBranch}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.DepositedBranch &&
                  formik.errors.DepositedBranch && (
                    <div className="error-label">
                      {formik.errors.DepositedBranch}
                    </div>
                  )}
              </div>

              <div className="col-md-4">
                <label className="form-label">
                  Deposited Mode <span className="text-danger">*</span>
                </label>
                <select
                  name="DepositedMode"
                  className={`form-select input-small ${
                    formik.touched.DepositedMode && formik.errors.DepositedMode
                      ? "is-invalid"
                      : ""
                  }`}
                  value={formik.values.DepositedMode || ""}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                >
                  <option value="">Select Mode</option>
                  {DEPOSITED_MODE.map((mode) => (
                    <option key={mode.key} value={mode.key}>
                      {mode.type}
                    </option>
                  ))}
                </select>
                {formik.touched.DepositedMode &&
                  formik.errors.DepositedMode && (
                    <div className="error-label">
                      {formik.errors.DepositedMode}
                    </div>
                  )}
              </div>

              <div className="col-md-4">
                <label className="form-label">
                  Account No <span className="text-danger">*</span>
                </label>
                <input
                  name="AccountNo"
                  className={`form-control input-small ${
                    formik.touched.AccountNo && formik.errors.AccountNo
                      ? "is-invalid"
                      : ""
                  }`}
                  value={formik.values.AccountNo}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.AccountNo && formik.errors.AccountNo && (
                  <div className="error-label">{formik.errors.AccountNo}</div>
                )}
              </div>
            </div>

            <div className="row mb-1">
              <div className="col-md-4">
                <label className="form-label">
                  Amount (₹) <span className="text-danger">*</span>
                </label>
                <input
                  type="number"
                  name="Amount"
                  className={`form-control input-small ${
                    formik.touched.Amount && formik.errors.Amount
                      ? "is-invalid"
                      : ""
                  }`}
                  value={formik.values.Amount || ""}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.Amount && formik.errors.Amount && (
                  <div className="error-label">{formik.errors.Amount}</div>
                )}
              </div>
              <div className="col-md-8">
                <label className="form-label">Remarks</label>
                <textarea
                  name="Remarks"
                  className={`form-control textarea-remarks ${
                    formik.touched.Remarks && formik.errors.Remarks
                      ? "is-invalid"
                      : ""
                  }`}
                  rows={3}
                  value={formik.values.Remarks}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.Remarks && formik.errors.Remarks && (
                  <div className="error-label">{formik.errors.Remarks}</div>
                )}
              </div>
            </div>
          </>
        );

      case "Other":
        return (
          <>
            <div className="row mb-3">
              <div className="col-md-4">
                <label className="form-label">
                  Other Date Of Transcation{" "}
                  <span className="text-danger">*</span>
                </label>
                <input
                  type="date"
                  name="TransactionDate"
                  className={`form-control input-small ${
                    formik.touched.TransactionDate &&
                    formik.errors.TransactionDate
                      ? "is-invalid"
                      : ""
                  }`}
                  value={(formik.values.TransactionDate || "").split("T")[0]}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.TransactionDate &&
                  formik.errors.TransactionDate && (
                    <div className="error-label">
                      {formik.errors.TransactionDate}
                    </div>
                  )}
              </div>

              <div className="col-md-4">
                <label className="form-label">
                  Amount (₹) <span className="text-danger">*</span>
                </label>
                <input
                  type="number"
                  name="Amount"
                  className={`form-control input-small ${
                    formik.touched.Amount && formik.errors.Amount
                      ? "is-invalid"
                      : ""
                  }`}
                  value={formik.values.Amount || ""}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.Amount && formik.errors.Amount && (
                  <div className="error-label">{formik.errors.Amount}</div>
                )}
              </div>

              <div className="col-md-4"></div>
            </div>

            <div className="row mb-1">
              <div className="col-md-12">
                <label className="form-label">Remarks</label>
                <textarea
                  name="Remarks"
                  className={`form-control textarea-remarks ${
                    formik.touched.Remarks && formik.errors.Remarks
                      ? "is-invalid"
                      : ""
                  }`}
                  rows={3}
                  value={formik.values.Remarks}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.Remarks && formik.errors.Remarks && (
                  <div className="error-label">{formik.errors.Remarks}</div>
                )}
              </div>
            </div>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <>
      {/* tabs */}
      <div className="d-flex gap-2 mb-4 type-tabs">
        {tabList.map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => handleTypeChange(t)}
            className={`tab-btn ${
              formik.values.Type === t ? "active-tab" : ""
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      <form onSubmit={formik.handleSubmit}>
        {renderFields()}

        <div className="d-flex gap-3 mt-4 justify-content-end">
          <button
            type="submit"
            disabled={formik.isSubmitting}
            className="btn-save"
          >
            {formik.isSubmitting ? "Saving..." : "Save"}
          </button>

          <button
            type="button"
            className="btn-cancel"
            onClick={() => {
              formik.resetForm();
              dispatch(clearTopupRequestDetails());
              if (isViewMode) {
                setIsEdit(false);
                dispatch(getTopupRequestByID(TopupID));
              } else {
                navigate(-1);
              }
            }}
          >
            Cancel
          </button>
        </div>
      </form>
    </>
  );
};

export default TopupRequestForm;
